// The shell's navigation (#749): five workspaces, each holding its pages.
//
// The flat menu #749 replaced held sixteen entries in one column, a list
// nobody could scan, and a page that needed a site sat beside the pages that
// worked, so an operator with nothing picked clicked into "Pick a site." five
// times over. The rail names the kind of work — Home, Accounts, Operate,
// Design, Admin — and the column beside it lists that workspace's pages, the
// ones ready at the current scope first and the ones waiting for a level of it
// under a heading that says which.
//
// **What a role may open is decided exactly as before**: `canAnywhere` for a
// capability, the superuser flag for a cell (§6.2). The server is the
// authority and refuses every capability itself; this only chooses what to
// draw, so that nobody is sent to a page the API would answer with 404 (§6.3).
// A workspace whose pages are all gated away is not drawn.
//
// **What a page needs from the scope is declared here once**, and
// `nav.test.tsx` holds each declaration to the page's own empty state, so the
// menu and the page cannot disagree about it. A page that needs a customer
// works at a site too, because a site names its customer.
//
// The current workspace is read from the address and stored nowhere, so a
// link, a reload and the back button all land on the right one. #749 changed
// no route: the sixteen addresses #749 found are the sixteen it left. Both
// counts above are #749's own and are not a tally of this file, which has
// gained pages since — #649's work list and #694's per-unit access.
//
// **Operate reads the network and Design changes it** (#874, ADR-0149). The
// pages that held both — Networks, Plan, Clients, Per-unit access, and the
// topology at the foot of the device list — became the tabs of Operate's
// Topology, Devices, Wireless and Access and of Design's Physical, Wired,
// VLANs, Wireless and Review. A tab is a segment of the page's address
// (`/design/changes/differences`), not a facet, because two of the pages it holds
// already spend `?tab=` on sections of their own: a device's page and the
// comparison. So a page owns every address under its own, and
// `workspaceKeyOf` matches the segment boundary rather than the whole path.
// Today's addresses are answered by `moved.ts`, which says where each went.
import type { components } from "../api/schema";
import { canAnywhere, type Capability } from "../authz";
import type { Scope } from "./scope";
import { NEUTRAL, type IconKey, type LexiconKey } from "../lexicon";
import type { VerticalKey } from "../verticals";
import {
  AccountsIcon, AdminIcon, AuditIcon, BrandReviewIcon, BrandsIcon, CarryOverIcon, CellsIcon, FleetMapIcon, ChangesIcon, CustomersIcon, DesignIcon, DeviceModelsIcon, DevicesIcon, FirmwareIcon, GatewayIcon, HardwareIcon, HomeIcon, IntegrationsIcon, OperateIcon, OrganizationsIcon, OverviewIcon, PhysicalIcon, ReportIcon, ServicePrincipalsIcon, SignInMethodsIcon, StockIcon, UsersIcon, VlansIcon, WiredIcon, WirelessIcon,
  type LucideIcon,
} from "../icons";

type Me = components["schemas"]["Me"];

/** The level of the scope a page cannot work without. */
export type Need = "customer" | "site";

export interface NavPage {
  to: string;
  /** The neutral word: what the rail says where the screen has no vertical,
   * and what a page that reads its own declaration titles itself. */
  label: string;
  /** A page that is a lexicon thing names its key, and the rail says the key's
   * plural in the vertical the screen reads (ADR-0477 3a, "menu labels"; #2033),
   * as it draws the key's icon. `label` is then the key's neutral plural,
   * taken from the table rather than written again. */
  word?: LexiconKey;
  /** One of the set's icons (#2043), or a lexicon key whose icon is the
   * screen's vertical's (ADR-0484): a page that is a lexicon thing names its
   * key, never the key's icon. */
  icon: LucideIcon | { term: IconKey };
  end?: boolean;
  permission?: Capability;
  // `superuser` rather than a capability: a cell sits outside every tenancy,
  // so there is no scope to grant a role at and the flag is the whole
  // permission (§4.2, ADR-0031 decision 2).
  superuser?: true;
  needs?: Need;
  /** Drawn only for a session with several organizations to manage (#2050):
   * a superuser, or one whose estate lists hold more than one. Not a
   * permission: the page opens by address for anyone it opened for before,
   * and what it shows is the server's answer (§6.3). */
  several?: "orgs";
  /** Drawn only for a superuser while the feature behind it is unfinished
   * (#2106, ruled 2026-10-28): arpIQ's staff go on building and testing it in
   * the same application, and nobody else is shown it. Not a permission, and
   * not `superuser` above, whose flag is the whole permission and is answered
   * 404 below it: this page opens by address for anyone it opened for before,
   * and what it shows is the server's answer (§6.2, §6.3). Removed from a page
   * by whichever item finishes its feature. */
  unfinished?: true;
}

export interface Workspace {
  key: string;
  label: string;
  /** One of the set's icons (#2043), drawn larger than a page's. */
  icon: LucideIcon;
  pages: NavPage[];
}

// The Portals page's address, named once: the declaration below and each
// vertical's order (PAGE_ORDER) both read it, so the lexicon's guard sees the
// address written once rather than once per vertical.
const PORTALS = "/design/portals";

export const WORKSPACES: Workspace[] = [
  { key: "home", label: "Home", icon: HomeIcon, pages: [
    { to: "/", label: "Overview", icon: OverviewIcon, end: true },
    // The sites and devices counted by what each site is (#2124, ADR-0498).
    // No capability: every role holds `site.read`, and the report counts the
    // sites the caller's grants reach, so it opens for anyone with a site.
    { to: "/reports/classification", label: "Classification", icon: ReportIcon },
  ] },
  // Sites first, so the workspace's entry lands there (#2050): an operator
  // comes to Accounts for its sites, and a one-row list of their own
  // organization was where it opened until then.
  { key: "estate", label: "Accounts", icon: AccountsIcon, pages: [
    { to: "/sites", label: NEUTRAL.site.many, word: "site", icon: { term: "site" } },
    { to: "/customers", label: "Customers", icon: CustomersIcon },
    // The list of organizations is for a session that has several (#1033 Q4,
    // ruled 2026-10-27): a superuser, or a provider whose lists hold a child
    // organization beside its own. A role at a parent organization reaches no
    // child organization (§6.2, ADR-0315), so a provider's lists hold a second
    // one exactly where it also holds a role there. An organization's own
    // settings are Admin's Organization page.
    { to: "/orgs", label: "Organizations", icon: OrganizationsIcon, several: "orgs" },
  ] },
  { key: "operate", label: "Operate", icon: OperateIcon, pages: [
    // The site's wired side, read (#1352): every wired port the site's
    // devices declare, and the topology — which is a tab of this page rather
    // than a section beside it, with `/operate/topology` redirecting to it
    // (`moved.ts`). One site's and never the fleet's (§2).
    //
    // `Wired` is the same word Design uses and names the same thing — ports
    // on whatever declares them, a switch's and an access point's alike
    // (§11.3) — so the two workspaces share it as they already share
    // `Wireless`. Reading it is here; authoring it is there.
    { to: "/operate/wired", label: "Wired", icon: WiredIcon, needs: "site" },
    // A customer's devices read across its sites, or one site's (ADR-0085).
    { to: "/operate/devices", label: "Devices", icon: DevicesIcon, needs: "customer" },
    // What the site's access points serve: its client devices today.
    { to: "/operate/wireless", label: "Wireless", icon: WirelessIcon, needs: "site" },
    // A building's per-unit access (#694, §11.4). `pan.read`, which every role
    // holds including viewer — a PAN carries no contact information and no
    // telemetry, so reading one is reading a unit's VLAN and expiry. What a
    // viewer does not hold is the reveal, and the page renders that control
    // from the capability rather than from the page being open.
    //
    // **Its label is not yet the lexicon's** (#2033): ADR-0477's neutral word
    // for the `pan` page is *Access*, while the table holds only the thing's,
    // *Per-unit access*, so routing it would rename the neutral page. The
    // icon follows the vertical; the word waits for the table to hold both.
    { to: "/operate/access", label: "Access", icon: { term: "pan" }, permission: "pan.read", needs: "site" },
    // The firmware Kiln catalogues for the site's access-point models (#1283,
    // ADR-0230). `superuser` rather than a capability: the read is held by the
    // flag and by no role (decision 6a) and answers 404 below it.
    { to: "/operate/firmware", label: "Firmware", icon: FirmwareIcon, superuser: true, needs: "site" },
    // Every site the person may read on one map, painted by one facet at a
    // time (#2214, ADR-0523): the Sites list's two reads, joined in the
    // browser. No capability and no scope: every role holds `site.read`, and
    // the reads list only the sites the caller's grants reach (§6.3).
    { to: "/operate/fleet-map", label: "Fleet map", icon: FleetMapIcon },
  ] },
  { key: "design", label: "Design", icon: DesignIcon, pages: [
    // What the site is meant to hold, beside what the cell holds (#519, ADR-0068).
    { to: "/design/physical", label: "Physical", icon: PhysicalIcon, needs: "site" },
    // The switches' cabling and their ports' intent (ADR-0045, ADR-0069, ADR-0127).
    { to: "/design/wired", label: "Wired", icon: WiredIcon, needs: "site" },
    // The customer's names for the site's tags (ADR-0064, ADR-0139).
    { to: "/design/vlans", label: "VLANs", icon: VlansIcon, needs: "site" },
    // The site's networks and radio intent (§11.1).
    { to: "/design/wireless", label: "Wireless", icon: WirelessIcon, needs: "site" },
    // A customer's portals, at the customer or at one of its sites (#485,
    // §12.1). `portal.read`, which every role holds; writing takes
    // `portal.write`, and the page draws its controls from that. Drawn for a
    // superuser alone while no deployment serves a guest through a portal
    // (#2106): production runs no guest-role zenith-aaa.
    { to: PORTALS, label: NEUTRAL.portal.many, word: "portal", icon: { term: "portal" }, permission: "portal.read", needs: "customer", unfinished: true },
    // A customer's warehouse: serials held before a site claims them (ADR-0482,
    // #2039). `site.read` reads it, which every role holds at its scope; the
    // page draws its controls from `device.write`.
    { to: "/design/warehouse", label: "Warehouse", icon: StockIcon, permission: "site.read", needs: "customer" },
    // The site's gateway router (#1519, §15, ADR-0290). `gateway.read`, which
    // every role holds; the page then shows what the read answers, including
    // that the deployment runs no gateway process (decision 2a). Drawn for a
    // superuser alone while production runs no zenith-gateway (#2106, #1549).
    { to: "/design/gateway", label: "Gateway", icon: GatewayIcon, permission: "gateway.read", needs: "site", unfinished: true },
    // The site's changes as one inbox (#1892; Review until then): the work list
    // (#649, ADR-0117) with one confirmation (ADR-0436), the comparison (#643,
    // ADR-0104) and the applies sent. Pending carries its own `network.write`,
    // because the read behind it takes it.
    { to: "/design/changes", label: "Changes", icon: ChangesIcon, needs: "site" },
    { to: "/carry-over", label: "Carry-over", icon: CarryOverIcon, needs: "site" },
    // No capability and no scope: a model is not tenant data, the API reads it
    // at every scope, and the page draws the same with no site picked (#354).
    { to: "/device-models", label: "Device models", icon: DeviceModelsIcon },
  ] },
  { key: "admin", label: "Admin", icon: AdminIcon, pages: [
    // The API contract is public without a session (§17.1), and a page that
    // hid it behind a role would mislead about what is protected (#404).
    { to: "/integrations", label: "Integrations", icon: IntegrationsIcon },
    // The organization in scope: its name and its brand (#2050). org.write,
    // which only an owner holds, because every control on the page writes the
    // organization.
    { to: "/organization", label: "Organization", icon: OrganizationsIcon, permission: "org.write" },
    { to: "/users", label: "Users", icon: UsersIcon, permission: "user.read" },
    // Who vouches for the people at an organization or a customer (ADR-0103
    // decision 2, #820). authmethod.write, which only an owner holds, because
    // every read behind the page takes it.
    { to: "/sign-in-methods", label: "Sign-in methods", icon: SignInMethodsIcon, permission: "authmethod.write" },
    // What an integration authenticates as (ADR-0172, #1005).
    // serviceprincipal.write, which only an owner holds, because every read
    // behind the page takes it.
    { to: "/service-principals", label: "Service principals", icon: ServicePrincipalsIcon, permission: "serviceprincipal.write" },
    { to: "/audit", label: "Audit", icon: AuditIcon, permission: "audit.read" },
    // The chains and brands an organization added, and the global list beside
    // them (#2110, ADR-0486). `site.write`, because adding one takes it
    // anywhere in the organization; editing one takes `catalogue.write`, and
    // the page draws that control from it.
    // `end`: Brand review's address is beneath it, and is its own page.
    { to: "/brands", label: "Brands", icon: BrandsIcon, permission: "site.write", end: true },
    // The staff acts and the queue (ADR-0486 decisions 5a and 6a): the
    // superuser flag, for the reason Cells is — every act on it answers 404
    // below the flag (§6.3).
    { to: "/brands/review", label: "Brand review", icon: BrandReviewIcon, superuser: true },
    { to: "/cells", label: "Cells", icon: CellsIcon, superuser: true },
    // Every hardware model and firmware build this deployment's cells run, and
    // what this service supports for each (#1938, ADR-0452). `superuser` for the
    // reason Cells is: the read covers every customer's devices and answers 404
    // below the flag (§6.3).
    { to: "/hardware", label: "Hardware", icon: HardwareIcon, superuser: true },
  ] },
];

/** Whether this person's role may open the page anywhere (§6.2). A page's
 * tab asks the same question of its own capability (`TabbedPage.tsx`). */
export function permitted(me: Me | undefined, p: Pick<NavPage, "permission" | "superuser">): boolean {
  if (p.superuser) return !!me?.user.superuser;
  return !p.permission || canAnywhere(me, p.permission);
}

/** Whether the scope names the level the page cannot work without. */
export function ready(scope: Scope, p: Pick<NavPage, "needs">): boolean {
  if (p.needs === "site") return !!scope.siteId;
  if (p.needs === "customer") return !!scope.customerId;
  return true;
}

/** Whether a page is drawn for this session, beside whether it may be opened.
 * A page drawn only for several organizations is drawn for a superuser always,
 * and for anyone else once the estate's lists have answered with more than
 * one; `orgs` is how many the lists hold, undefined while they have not
 * answered. A page whose feature is unfinished is drawn for a superuser alone
 * (#2106). It reads the session's own flag and lists and decides nothing about
 * access, which stays `permitted`'s and the server's (§6.2). */
export function drawn(me: Me | undefined, p: Pick<NavPage, "several" | "unfinished">, orgs: number | undefined): boolean {
  if (p.unfinished && !showsUnfinished(me)) return false;
  if (p.several !== "orgs") return true;
  return !!me?.user.superuser || (orgs ?? 0) > 1;
}

/** Whether this session is shown a feature that is unfinished (#2106): a
 * superuser alone. Presentation only, like `drawn`: the server decides what a
 * session reaches (§6.2), and a control this hides is still the API's to
 * answer for whoever calls it. */
export function showsUnfinished(me: Me | undefined): boolean {
  return !!me?.user.superuser;
}

/** The workspaces this person may open, each with only its permitted pages
 * and those drawn for how many organizations the lists hold. A workspace left
 * with none is not returned. */
export function workspacesFor(me: Me | undefined, from: Workspace[] = WORKSPACES, orgs?: number): Workspace[] {
  return from
    .map((w) => ({ ...w, pages: w.pages.filter((p) => permitted(me, p) && drawn(me, p, orgs)) }))
    .filter((w) => w.pages.length > 0);
}

/** Whether `pathname` is the page's address or one of its tabs'
 * (`/design/changes/differences` is Changes'). The boundary is a segment, so
 * `/design/wireless` does not own `/design/wirelessly`; Overview's `/` owns
 * only itself. */
export function owns(p: Pick<NavPage, "to">, pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return path === p.to || (p.to !== "/" && path.startsWith(p.to + "/"));
}

/** The workspace an address belongs to: the one holding a page at that path,
 * or Home for an address no page holds (the not-found page). */
export function workspaceKeyOf(pathname: string): string {
  return WORKSPACES.find((w) => w.pages.some((p) => owns(p, pathname)))?.key ?? "home";
}

/** The page at an address, for a page that reads its own declaration — its
 * label and what it needs from the scope — rather than repeating it. */
export function pageAt(to: string): NavPage {
  const p = WORKSPACES.flatMap((w) => w.pages).find((x) => x.to === to);
  if (!p) throw new Error(`nav: no page is declared at ${to}`);
  return p;
}

/** The order of pages within a workspace, by vertical (ADR-0477 decision 6a,
 * #2067), ruled as content on #2067 (note 4103642163) and changed as copy by
 * the same kind of ruling. Each list names the pages after the workspace's
 * first, which stays first; Home, Accounts and Admin keep one order for
 * everyone. **An order only moves pages**: the pages drawn are `workspacesFor`'s,
 * so a vertical never adds, hides or moves one across Operate and Design
 * (ADR-0149), and never changes which a role reaches. `nav.test.tsx` holds each
 * list to its workspace's pages. */
export const PAGE_ORDER: Record<VerticalKey, { operate: string[]; design: string[] }> = {
  hospitality: {
    operate: ["/operate/wireless", "/operate/access", "/operate/devices", "/operate/firmware", "/operate/fleet-map"],
    design: ["/design/wireless", PORTALS, "/design/vlans", "/design/wired", "/design/changes", "/design/gateway", "/design/warehouse", "/carry-over", "/device-models"],
  },
  senior: {
    operate: ["/operate/access", "/operate/wireless", "/operate/devices", "/operate/firmware", "/operate/fleet-map"],
    design: ["/design/wireless", PORTALS, "/design/wired", "/design/vlans", "/design/changes", "/design/gateway", "/design/warehouse", "/carry-over", "/device-models"],
  },
  mdu: {
    operate: ["/operate/access", "/operate/devices", "/operate/wireless", "/operate/firmware", "/operate/fleet-map"],
    design: ["/design/wireless", "/design/vlans", "/design/wired", PORTALS, "/design/changes", "/design/gateway", "/design/warehouse", "/carry-over", "/device-models"],
  },
  corporate: {
    operate: ["/operate/devices", "/operate/wireless", "/operate/access", "/operate/firmware", "/operate/fleet-map"],
    design: ["/design/wired", "/design/vlans", "/design/wireless", "/design/gateway", "/design/changes", PORTALS, "/design/warehouse", "/carry-over", "/device-models"],
  },
  retail: {
    operate: ["/operate/devices", "/operate/wireless", "/operate/firmware", "/operate/access", "/operate/fleet-map"],
    design: ["/design/wireless", "/design/wired", "/design/vlans", "/design/changes", PORTALS, "/design/gateway", "/design/warehouse", "/carry-over", "/device-models"],
  },
};

/** A workspace's pages as the rail lists them in the vertical the screen
 * reads: the first page first, the rest in that vertical's order, and a page
 * the order does not name after those, as declared. Neutral, or a workspace
 * with no order, lists them as declared. */
export function inOrder(w: Workspace, vertical: VerticalKey | undefined): NavPage[] {
  const order = vertical ? (PAGE_ORDER[vertical] as Record<string, string[] | undefined>)[w.key] : undefined;
  if (!order || w.pages.length === 0) return w.pages;
  const [first, ...rest] = w.pages;
  const at = (p: NavPage) => { const i = order.indexOf(p.to); return i < 0 ? order.length : i; };
  return [first!, ...rest.sort((a, b) => at(a) - at(b))];
}

/** Where a workspace's own entry leads: its first page that works at this
 * scope, else its first page — which then says what it is waiting for. It is
 * asked of the workspace as declared and never of `inOrder`'s list, because
 * the vertical does not choose the page a workspace opens on (ADR-0477 6b,
 * not taken). */
export function entryOf(w: Workspace, scope: Scope): NavPage {
  return w.pages.find((p) => ready(scope, p)) ?? w.pages[0]!;
}

/** The heading over a workspace's pages that wait for a level of the scope. */
export function waitingHeading(need: Need): string {
  return need === "site" ? "Choose a site to open" : "Choose a customer to open";
}
